import { useMutation, useQuery } from '@apollo/client'
import { AddCircle, Delete, Edit } from '@mui/icons-material'
import {
    Box,
    Button,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { gql } from '__generated__'
import { CandidateAnalysts } from '__generated__/graphql'
import { format } from 'date-fns'
import { type FormikConfig, useFormik } from 'formik'
import { type FC, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ProfilePanel } from 'recruiter/components/ProfilePanel'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import useToast from 'shared/components/Toast/useToast'
import { formatGraphQLError, getFullName, getPxAsRem } from 'shared/utils'
import * as Yup from 'yup'

const GetCandidateAnalysts = gql(/* GraphQL */ `
    query GetCandidateAnalysts($candidateId: ID) {
        candidate(id: $candidateId) {
            id
            otherAnalysts {
                id
                analyst
                updatedAt
                updatedByUser {
                    id
                    firstName
                    nickname
                    lastName
                }
            }
        }
    }
`)

const UpdateCandidateAnalysts = gql(/* GraphQL */ `
    mutation UpdateCandidateAnalysts($analysts: [String!]!, $candidateId: ID!) {
        updateCandidateAnalysts(
            analysts: $analysts
            candidateId: $candidateId
        ) {
            # TODO: This should be returning the standard "success" and "message" fields
            candidateId
            analyst
        }
    }
`)

interface FormValue extends Pick<CandidateAnalysts, 'analyst'> {}

const validationSchema: Yup.ArraySchema<FormValue[], Yup.AnyObject, unknown> =
    Yup.array()
        .of(
            Yup.object({
                analyst: Yup.string().required('Analyst name is required'),
            })
        )
        .required()

interface CandidateOtherAnalystsPanelProps {}

export const CandidateOtherAnalystsPanel: FC<
    CandidateOtherAnalystsPanelProps
> = () => {
    const { candidateId } = useParams()
    const toast = useToast()
    const [isBeingEdited, setIsBeingEdited] = useState(false)
    const [initialFormValues, setInitialFormValues] = useState<FormValue[]>([])
    const [auditString, setAuditString] = useState('')

    const { loading: isLoading } = useQuery(GetCandidateAnalysts, {
        fetchPolicy: 'cache-and-network',
        variables: { candidateId },
        onCompleted: (data) => {
            setInitialFormValues(
                data.candidate?.otherAnalysts?.map(({ analyst }) => ({
                    analyst,
                })) || []
            )

            // Note that technically we don't have audit data the list of
            // analysts as a whole; we just use the first entry's audit data (if
            // applicable) as a rough approximation.
            const auditUser = data.candidate?.otherAnalysts?.[0]?.updatedByUser
            const auditDate = new Date(
                data.candidate?.otherAnalysts?.[0]?.updatedAt
            )

            setAuditString(
                `Last Edited: ${format(auditDate, 'MM/dd/yyyy hh:mm a')} ${auditUser ? getFullName(auditUser) : 'Unknown User'}`
            )
        },
    })

    const [updateCandidateAnalysts] = useMutation(UpdateCandidateAnalysts, {
        refetchQueries: [GetCandidateAnalysts],
        onCompleted: (data) => {
            // TODO: This is what we should do once we have the typical
            // success/message mutation response:
            // if (data.updateCandidateAnalysts.success) {
            //     toast({
            //         title: 'Success!',
            //         message:
            //             'Candidate analysts has been updated successfully',
            //         severity: 'success',
            //     })
            // } else {
            //     toast({
            //         title: 'Error',
            //         message: `Failed to update candidate analysts: ${data.updateCandidateAnalysts.message}`,
            //         severity: 'error',
            //     })
            // }

            // TODO: Remove this once we have the typical mutation
            // success/message response:
            toast({
                title: 'Info',
                message: `Attempted to update candidate analysts`,
                severity: 'info',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                severity: 'error',
                message: formatGraphQLError(
                    error,
                    'Failed to update candidate analysts'
                ),
            })
        },
    })

    const handleAddCandidateAnalystEntry = () => {
        setIsBeingEdited(true)
        formik.setValues((values) => [...values, { analyst: '' }])
    }

    const handleRemoveCandidateAnalystEntry = (index: number) => {
        formik.setValues((values) => {
            const newValues = [...values]
            newValues.splice(index, 1)
            return newValues
        })
    }

    const handleCancel = () => {
        formik.resetForm()
        setIsBeingEdited(false)
    }

    const handleUpdateCandidateAnalysts: FormikConfig<
        FormValue[]
    >['onSubmit'] = (values) => {
        updateCandidateAnalysts({
            variables: {
                candidateId: candidateId || '',
                analysts: values.map(({ analyst }) => analyst),
            },
        })
        setIsBeingEdited(false)
    }

    const formik = useFormik<FormValue[]>({
        initialValues: initialFormValues,
        validationSchema,
        onSubmit: handleUpdateCandidateAnalysts,
        enableReinitialize: true,
    })

    return (
        <ProfilePanel
            panelTitle="Other Analysts in Class"
            panelSubtitle={auditString}
            {...(formik.values.length > 0 && {
                actionElements: [
                    <IconButton
                        key="edit"
                        onClick={() => setIsBeingEdited(true)}
                        aria-label="Edit analysts"
                    >
                        <Edit fontSize="small" />
                    </IconButton>,
                ],
            })}
        >
            {isLoading && (
                <Box flex="1">
                    <LoadingPanel />
                </Box>
            )}

            {!isLoading &&
                isBeingEdited &&
                formik.values.map((value, index) => (
                    <Stack direction="row" gap={getPxAsRem(4)} key={index}>
                        <TextField
                            error={
                                formik.touched[index]?.analyst &&
                                Boolean(formik.errors[index]?.analyst)
                            }
                            fullWidth
                            helperText={
                                formik.touched[index]?.analyst &&
                                formik.errors[index]?.analyst
                            }
                            label="Full Name"
                            name={`${index}.analyst`}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            value={formik.values[index].analyst}
                        />

                        <IconButton
                            sx={{ borderRadius: 1, alignSelf: 'flex-start' }}
                            onClick={() =>
                                handleRemoveCandidateAnalystEntry(index)
                            }
                            aria-label="Remove analyst"
                        >
                            <Delete />
                        </IconButton>
                    </Stack>
                ))}

            {!isLoading && !isBeingEdited && formik.values.length > 0 && (
                <Stack gap={getPxAsRem(4)}>
                    {formik.values.map(({ analyst }) => (
                        <Typography key={analyst} noWrap>
                            {analyst}
                        </Typography>
                    ))}
                </Stack>
            )}

            {!isLoading && !isBeingEdited && formik.values.length < 1 && (
                <Typography>-</Typography>
            )}

            {!isLoading && (
                <Button
                    onClick={handleAddCandidateAnalystEntry}
                    variant="text"
                    startIcon={<AddCircle />}
                    sx={{ marginTop: 'auto', alignSelf: 'flex-start' }}
                >
                    Add Analyst
                </Button>
            )}

            {!isLoading && isBeingEdited && (
                <Stack direction="row" gap={getPxAsRem(8)}>
                    <Button variant="outlined" onClick={handleCancel}>
                        Cancel
                    </Button>

                    <Button
                        disabled={!formik.dirty}
                        onClick={formik.submitForm}
                    >
                        Save & Update
                    </Button>
                </Stack>
            )}
        </ProfilePanel>
    )
}
